import {
  getAllowLogo,
  getAllowText,
  getCanvasPreview,
  getCanvasResizeInfo,
  getCurrentCanvasData,
  getCurrentLogo,
  getImprintElementExistStatus,
  getSelectedImage,
  getSideBarWidth,
} from '../canvas/selectors';
import { getAllowUserImageIcons, getInnerOrOuterSlogan } from '../userimage/selectors';
import {
  getCanvasOverVignetteConfig,
  getCurrentImprintAreaCode,
  getCurrentImprintAreaKey,
  getLogoEditingStatus,
  getSidebarTabKey,
  getStudioItemType,
  getTempBackgroundImage,
} from '../main/selectors';
import { getIfCommonTemplateDictIsNotEmpty, getSpecialTemplates } from '../template/selectors';
import {
  getImprintOptionOfferOutSideSlogan,
  getImprintSpecs,
  getSentimentAvailable,
  getVerseAvailable,
} from '../imprintarea/selectors';

import { TemplateType } from '../template/constants';
import { createSelector } from 'reselect';
import { getAllSelectedTextElements } from '../text/selectors';
import { isMobileOnly } from 'react-device-detect';
import { sidebarTabKeyEnum } from '../utils/constants';
import { studioItemType as studioItemTypeEnum } from '../main/constants';

export const getSidebarStoreData = createSelector(
  getCurrentImprintAreaKey,
  getSidebarTabKey,
  getStudioItemType,
  (state) => getInnerOrOuterSlogan(state, true),
  getAllowText,
  getAllowLogo,
  getSpecialTemplates,
  getImprintElementExistStatus,
  getTempBackgroundImage,
  getSideBarWidth,
  getLogoEditingStatus,
  (state) =>
    getStudioItemType(state) === studioItemTypeEnum.digital ? getSelectedImage(state) : getCurrentLogo(state),
  getAllSelectedTextElements,
  getCanvasOverVignetteConfig,
  getCanvasResizeInfo,
  getCurrentCanvasData,
  getCanvasPreview,
  (
    canvasKey,
    currentSidebarTabKey,
    studioItemType,
    outsideSloganList,
    digitalAllowText,
    digitalAllowLogo,
    specialTemplates,
    elementExistStatus,
    tempBackgroundImage,
    sideBarWidth,
    logoEditingStatus,
    selectedImage,
    selectedTextElements,
    canvasOverVignetteConfig,
    canvasResizeInfo,
    canvasData,
    canvasPreview
  ) => {
    return {
      canvasKey,
      currentSidebarTabKey,
      studioItemType,
      outsideSloganList,
      digitalAllowText,
      digitalAllowLogo,
      specialTemplates,
      elementExistStatus,
      tempBackgroundImage,
      sideBarWidth,
      logoEditingStatus,
      selectedImage,
      selectedTextElements,
      canvasOverVignetteConfig,
      canvasResizeInfo,
      canvasData,
      canvasPreview,
    };
  }
);

export const getAllowedIcons = createSelector(
  getStudioItemType,
  getAllowText,
  getAllowLogo,
  getAllowUserImageIcons,
  getIfCommonTemplateDictIsNotEmpty,
  getSpecialTemplates,
  (state) => getImprintSpecs(state, getCurrentImprintAreaKey(state)),
  (state) => getImprintOptionOfferOutSideSlogan(state, getCurrentImprintAreaCode(state)),
  getVerseAvailable,
  getSentimentAvailable,
  (
    studioItemType,
    digitalAllowText,
    digitalAllowLogo,
    allowUserImageIcons,
    commonTemplateDictIsNotEmpty,
    specialTemplates,
    imprintAllowLogoText,
    allowOutsideSlogan,
    verseAvailable,
    sentimentAvailable
  ) => {
    const inputParams = {
      studioItemType,
      commonTemplateDictIsNotEmpty,
      specialTemplates,
      imprintAllowLogoText,
      digitalAllowLogo,
      allowUserImageIcons,
      digitalAllowText,
      allowOutsideSlogan,
      sentimentAvailable,
      verseAvailable,
    };
    const { showBackgroundIcon, showLogoIcon, showOutsideSloganIcon, showSentimentIcon, showVerseIcon, showTextIcon } =
      checkIcons(inputParams);

    const iconKeys = [];
    let count = 0;
    if (showBackgroundIcon) iconKeys[count++] = sidebarTabKeyEnum.Background;
    if (showLogoIcon) iconKeys[count++] = sidebarTabKeyEnum.Logo;
    if (showOutsideSloganIcon) iconKeys[count++] = sidebarTabKeyEnum.OutsideSlogan;
    if (showSentimentIcon) iconKeys[count++] = sidebarTabKeyEnum.Sentiment;
    if (showVerseIcon) iconKeys[count++] = sidebarTabKeyEnum.Verse;
    if (showTextIcon) iconKeys[count++] = sidebarTabKeyEnum.Text;
    if (isMobileOnly) iconKeys[count++] = sidebarTabKeyEnum.Preview;

    return iconKeys;
  }
);

const checkIcons = (inputParams) => {
  let showBackgroundIcon = false,
    showLogoIcon = false,
    showTextIcon = false,
    showOutsideSloganIcon = false,
    showSentimentIcon = false,
    showVerseIcon = false;

  switch (inputParams.studioItemType) {
    case studioItemTypeEnum.nonDigital:
      showLogoIcon = inputParams.imprintAllowLogoText.logo;
      showTextIcon = inputParams.imprintAllowLogoText.text;
      showOutsideSloganIcon = inputParams.allowOutsideSlogan;
      showSentimentIcon = inputParams.sentimentAvailable;
      showVerseIcon = inputParams.verseAvailable;
      break;

    case studioItemTypeEnum.digital:
      showBackgroundIcon =
        inputParams.commonTemplateDictIsNotEmpty ||
        inputParams.specialTemplates.find(
          (template) =>
            template.templateType === TemplateType.CUSTOM ||
            template.templateType === TemplateType.DEFAULT ||
            template.templateType === TemplateType.BLANK
        ) !== undefined;

      if (
        inputParams.specialTemplates.length === 1 &&
        inputParams.specialTemplates[0].templateType === TemplateType.BLANK
      ) {
        showBackgroundIcon = false;
      }

      showLogoIcon = inputParams.digitalAllowLogo && inputParams.allowUserImageIcons.length !== 0;
      showTextIcon = inputParams.digitalAllowText;
      break;

    case studioItemTypeEnum.screenPrintedDigital:
      showLogoIcon = inputParams.digitalAllowLogo;
      showTextIcon = inputParams.digitalAllowText;
      showOutsideSloganIcon = inputParams.allowOutsideSlogan;
      showSentimentIcon = inputParams.sentimentAvailable;
      showVerseIcon = inputParams.verseAvailable;
      break;

    default:
      break;
  }

  return { showBackgroundIcon, showLogoIcon, showOutsideSloganIcon, showSentimentIcon, showVerseIcon, showTextIcon };
};

export const getAllowedIconsChecker = createSelector(getAllowedIcons, (allowedIcons) => {
  return allowedIcons.reduce((pre, cur) => {
    return pre + cur;
  }, 0);
});
